import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Dialog } from "primereact/dialog";
import { Message } from "primereact/message";
import { ProgressSpinner } from "primereact/progressspinner";
import { SplitButton } from "primereact/splitbutton";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  activateCatBatch,
  catResults,
  initiateRmsEltRequest,
  rmsEltRequestStatus,
  rmsEltJobHistory,
  downloadAllRmsEltArtifacts,
  rerunCatFinancials,
  resumeCatPipeline,
  type EltPipelineType,
  type CatVendorType,
  initiateRms22Request,
  rms22RequestStatus,
  rms22JobHistory,
  downloadAllRms22Artifacts,
  initiateIcmYltRequest,
  icmYltRequestStatus,
  icmYltJobHistory,
  downloadAllIcmYltArtifacts,
  initiateSpatialKatRequest,
  spatialKatRequestStatus,
  spatialKatJobHistory,
  downloadAllSpatialKatArtifacts,
} from "../../../api/routes";
import { useCatModelPolling } from "./useCatModelPolling";
import { SafeReportResponse } from "../Conversion";
import styles from "./CatCard.module.css";
import {
  WorkStatus,
  CatResultsResponse,
  CatJobHistoryItem,
  CatJobHistoryResponse,
  CatPrecheckResponse,
  CatRunChange,
  VendorPrecheck,
} from "../../../client/types.gen";
import { useToast } from "../../../context-providers/Toast";
import { downloadBlob } from "../../../util";
import { apiErrorDetail } from "../../../api/errors";
import { useFeatureEnabled } from "../../../IfFeatureEnabled";
import {
  useEnqueueEqbdRater,
  useEnqueueRater,
  useLocalUser,
  usePrecheck,
} from "../data";
import {
  CatRunMode,
  ModelTabOption,
  SelectedModel,
  getPerilDisplayName,
  selectedModelToAuditSource,
} from "./types";
import { useIsMobile } from "../../../responsive/useMediaQuery";
import { RaterTab } from "./RaterTab";
import { EqbdRaterTab } from "./EqbdRaterTab";
import { useReportJobs } from "../rater/useIntegrationJobs";
import { CatModelSwitcher, ModelTabStatus } from "./CatModelSwitcher";
import { CatResultsView } from "./CatResultsView";
import { ModelRunComparisonDialog } from "./ModelRunComparisonDialog";
import { CatRunStatusIndicator } from "./CatRunStatusIndicator";
import { TemplateButton } from "../template/TemplateButton";
import { ParsedTemplate } from "../template/data";

interface CatCardProps {
  specialtyPropertyInfo: SafeReportResponse;
}

const LARGE_CAT_JOB_THRESHOLD = 10_000;

// Each vendor's run history comes from its own endpoint, so refreshing it
// after acting on a row is dispatched per model.
const JOB_HISTORY_FETCHER: Record<
  SelectedModel,
  (reportId: string) => Promise<CatJobHistoryResponse>
> = {
  rms_elt: rmsEltJobHistory,
  rms_22: rms22JobHistory,
  icm_ylt: icmYltJobHistory,
  spatialkat: spatialKatJobHistory,
};

export const CatCard: React.FC<CatCardProps> = ({ specialtyPropertyInfo }) => {
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [rmsEltResultsData, setRmsEltResultsData] =
    useState<CatResultsResponse | null>(null);
  const [rms22ResultsData, setRms22ResultsData] =
    useState<CatResultsResponse | null>(null);
  const [icmYltResultsData, setIcmYltResultsData] =
    useState<CatResultsResponse | null>(null);
  const [spatialKatResultsData, setSpatialKatResultsData] =
    useState<CatResultsResponse | null>(null);
  const [jobHistory, setJobHistory] = useState<CatJobHistoryItem[]>([]);
  const [viewingBatchId, setViewingBatchId] = useState<string | null>(null);
  // Roof source of the viewed batch; drives the per-peril "Enhanced · <source>"
  // badge. Null for a broker-data run.
  const [viewingEnhancedSource, setViewingEnhancedSource] = useState<
    string | null
  >(null);
  const [enrichLoading, setEnrichLoading] = useState(false);
  const [compareBaseJob, setCompareBaseJob] =
    useState<CatJobHistoryItem | null>(null);
  const [searchParams] = useSearchParams();
  const [selectedModel, setSelectedModel] = useState<ModelTabOption>(
    searchParams.get("model") === "rater" ? "rater" : "rms_elt"
  );
  const [showAllLayers, setShowAllLayers] = useState(false);
  const isMobile = useIsMobile();
  const toast = useToast();
  const user = useLocalUser();
  const catModelOverrideAllowed = useFeatureEnabled(
    "cat-model-override-allowed",
    undefined,
    user
  );
  const largeCatJobsAllowed = useFeatureEnabled(
    "large-cat-jobs-allowed",
    undefined,
    user
  );
  const rmsRerunFinancialsEnabled = useFeatureEnabled(
    "rms-rerun-financials",
    undefined,
    user
  );
  const catShadowRunAllowed = useFeatureEnabled(
    "cat-shadow-run",
    undefined,
    user
  );
  const catStalenessUiEnabled = useFeatureEnabled(
    "cat-staleness-ui",
    undefined,
    user
  );
  const catRunModeOverrideAllowed = useFeatureEnabled(
    "cat-run-mode-override",
    undefined,
    user
  );
  const catActivateRunAllowed = useFeatureEnabled(
    "cat-activate-run",
    undefined,
    user
  );
  // The batch whose resume / re-run-financials is currently being kicked
  // off, so the history row can show a spinner and the rest disable.
  const [recoveryInFlightBatchId, setRecoveryInFlightBatchId] = useState<
    string | null
  >(null);
  const [activatingBatchId, setActivatingBatchId] = useState<string | null>(
    null
  );
  const numLocations = specialtyPropertyInfo.precomputed_metadata.num_locations;
  const isLargeJob = numLocations > LARGE_CAT_JOB_THRESHOLD;
  const [showLargeJobBlockedDialog, setShowLargeJobBlockedDialog] =
    useState(false);
  const [showRunModelingDialog, setShowRunModelingDialog] = useState(false);
  const [runModelingRms, setRunModelingRms] = useState(true);
  const [runModelingKatRisk, setRunModelingKatRisk] = useState(true);
  const [runModelingIcm, setRunModelingIcm] = useState(true);
  const [runModelingRater, setRunModelingRater] = useState(true);
  const [runModelingEqbd, setRunModelingEqbd] = useState(true);
  const [raterTemplateOverride, setRaterTemplateOverride] = useState<
    ParsedTemplate | undefined
  >(undefined);
  const guardLargeJob = (action: () => void) => {
    if (isLargeJob && !largeCatJobsAllowed) {
      setShowLargeJobBlockedDialog(true);
      return;
    }
    action();
  };
  const reportId = specialtyPropertyInfo.report_id;

  const handleJobHistoryUpdate = useCallback(
    (modelType: string, jobs: CatJobHistoryItem[]) => {
      setJobHistory((prev) => {
        const otherJobs = prev.filter((j) => j.type !== modelType);
        return [...otherJobs, ...jobs].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );
      });
    },
    []
  );

  const {
    data: precheckData,
    isFetching: precheckFetching,
    refetch: refetchPrecheck,
  } = usePrecheck(reportId);

  const rmsEltPolling = useCatModelPolling({
    modelType: "rms_elt",
    displayName: "RMS",
    reportId,
    toast,
    // Baseline runs go on broker data (null enrichment) and carry the run-mode
    // override when one is set.
    initiateRequest: (reportId, activate, mode) =>
      initiateRmsEltRequest(reportId, activate, null, mode),
    checkStatus: rmsEltRequestStatus,
    fetchJobHistory: rmsEltJobHistory,
    onComplete: async () => {
      setRmsEltResultsData(await catResults(reportId));
      refetchPrecheck();
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  // "Shadow run → Enhanced": run the existing RMS ELT pipeline with enrichment
  // as a shadow run (never activates), recorded as run metadata. It shows up in
  // the RMS Run History tagged "Enhanced"; results are opened from there. Reuses
  // rmsEltPolling for status polling since it's the same RMS_ELT slot.
  const handleEnhancedShadowRun = (enrichmentSource: "nearmap" | "cape") =>
    guardLargeJob(async () => {
      setEnrichLoading(true);
      try {
        const resp = await initiateRmsEltRequest(
          reportId,
          false,
          enrichmentSource
        );
        rmsEltPolling.setPendingRequestIds([resp.request_id]);
        const hist = await rmsEltJobHistory(reportId);
        handleJobHistoryUpdate("rms_elt", hist.jobs ?? []);
      } catch {
        toast.show({
          severity: "error",
          summary: "Enhanced RMS Failed",
          detail: "There was an error starting the enhanced RMS run.",
        });
      } finally {
        setEnrichLoading(false);
      }
    });

  const rms22Polling = useCatModelPolling({
    modelType: "rms_22",
    displayName: "RMS 22",
    reportId,
    toast,
    initiateRequest: initiateRms22Request,
    checkStatus: rms22RequestStatus,
    fetchJobHistory: rms22JobHistory,
    onComplete: async () => {
      const results = await catResults(reportId);
      setRms22ResultsData(results);
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  const icmYltPolling = useCatModelPolling({
    modelType: "icm_ylt",
    displayName: "ICM",
    reportId,
    toast,
    // Baseline runs go on broker data (null enrichment) and carry the run-mode
    // override when one is set.
    initiateRequest: (reportId, activate, mode) =>
      initiateIcmYltRequest(reportId, activate, null, mode),
    checkStatus: icmYltRequestStatus,
    fetchJobHistory: icmYltJobHistory,
    onComplete: async () => {
      setIcmYltResultsData(await catResults(reportId));
      refetchPrecheck();
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
  });

  // "Shadow run → Enhanced" for ICM: run the ICM YLT pipeline with enrichment
  // as a shadow run (never activates), recorded as run metadata. It shows up in
  // the ICM Run History tagged "Enhanced"; results are opened from there. Reuses
  // icmYltPolling for status polling since it's the same ICM_YLT slot.
  const handleEnhancedIcmShadowRun = (enrichmentSource: "nearmap" | "cape") =>
    guardLargeJob(async () => {
      setEnrichLoading(true);
      try {
        const resp = await initiateIcmYltRequest(
          reportId,
          false,
          enrichmentSource
        );
        icmYltPolling.setPendingRequestIds([resp.request_id]);
        const hist = await icmYltJobHistory(reportId);
        handleJobHistoryUpdate("icm_ylt", hist.jobs ?? []);
      } catch {
        toast.show({
          severity: "error",
          summary: "Enhanced ICM Failed",
          detail: "There was an error starting the enhanced ICM run.",
        });
      } finally {
        setEnrichLoading(false);
      }
    });

  const spatialKatPolling = useCatModelPolling({
    modelType: "spatialkat",
    displayName: "SpatialKat",
    reportId,
    toast,
    initiateRequest: initiateSpatialKatRequest,
    checkStatus: spatialKatRequestStatus,
    fetchJobHistory: spatialKatJobHistory,
    onComplete: async () => {
      setSpatialKatResultsData(await catResults(reportId));
      refetchPrecheck();
    },
    onJobHistoryUpdate: handleJobHistoryUpdate,
    showErrorDetail: true,
  });

  const precheck = precheckData?.cat;
  const runStatusFor = (
    isRunning: boolean,
    change: CatRunChange | undefined
  ): ModelTabStatus | undefined =>
    isRunning ? "running" : precheckFetching ? "loading" : change;

  // Excludes RMS 22 (not part of the normal flow) and the rater tabs, which
  // have no staleness concept.
  const runStatusByModel:
    | Partial<Record<ModelTabOption, ModelTabStatus>>
    | undefined = catStalenessUiEnabled
    ? {
        rms_elt: runStatusFor(
          rmsEltPolling.isPending,
          precheck?.rms.results_change.change
        ),
        spatialkat: runStatusFor(
          spatialKatPolling.isPending,
          precheck?.spatialkat.results_change.change
        ),
        icm_ylt: runStatusFor(
          icmYltPolling.isPending,
          precheck?.icm.results_change.change
        ),
      }
    : undefined;
  const ratePrecheck = precheckData?.rater;
  const { jobs: raterJobs } = useReportJobs(reportId, {
    integrationTypes: ["RATER"],
  });
  const raterBlocked =
    !ratePrecheck || ratePrecheck.warnings.some((w) => w.blocking);
  const hasRaterResults = raterJobs.some((j) => j.outcome === "success");

  const runRater = useEnqueueRater(reportId);
  const handleRunRater = async () => {
    try {
      await runRater.mutateAsync(raterTemplateOverride?.template_id);
    } catch {
      toast.show({
        severity: "error",
        summary: "Rater Request Failed",
        detail: "There was an error starting your rater run.",
      });
    }
  };

  const { jobs: eqbdRaterJobs } = useReportJobs(reportId, {
    integrationTypes: ["EQBD_RATER"],
  });
  const hasEqbdResults = eqbdRaterJobs.some((j) => j.outcome === "success");
  const eqbdRaterPending = eqbdRaterJobs.some((j) => !j.outcome);
  const runEqbdRater = useEnqueueEqbdRater(reportId);
  const handleRunEqbdRater = async () => {
    try {
      await runEqbdRater.mutateAsync();
    } catch {
      toast.show({
        severity: "error",
        summary: "Equipment Breakdown Rater Failed",
        detail: "There was an error starting your EB rater run.",
      });
    }
  };

  // Load existing results and check for pending jobs on mount
  useEffect(() => {
    const loadExistingResults = async () => {
      try {
        const [catRes, rmsEltHist, rms22Hist, icmYltHist, skHist] =
          await Promise.all([
            catResults(specialtyPropertyInfo.report_id).catch(() => null),
            rmsEltJobHistory(specialtyPropertyInfo.report_id),
            rms22JobHistory(specialtyPropertyInfo.report_id).catch(
              () => ({ jobs: [] }) as CatJobHistoryResponse
            ),
            icmYltJobHistory(specialtyPropertyInfo.report_id).catch(
              () => ({ jobs: [] }) as CatJobHistoryResponse
            ),
            spatialKatJobHistory(specialtyPropertyInfo.report_id),
          ]);

        if (catRes?.spatialkat?.aggregate?.layers?.length) {
          setSpatialKatResultsData(catRes);
        }

        if (catRes?.rms_elt?.aggregate?.layers?.length) {
          setRmsEltResultsData(catRes);
        }

        if (catRes?.rms_22?.aggregate?.layers?.length) {
          setRms22ResultsData(catRes);
        }

        if (catRes?.icm_ylt?.aggregate?.layers?.length) {
          setIcmYltResultsData(catRes);
        }

        // Combine job histories
        const allJobs = [
          ...(rmsEltHist.jobs ?? []),
          ...(rms22Hist.jobs ?? []),
          ...(icmYltHist.jobs ?? []),
          ...(skHist.jobs ?? []),
        ].sort(
          (a, b) =>
            new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
        );

        setJobHistory(allJobs);

        // Resume polling for any active jobs
        const getActiveIds = (hist: { jobs?: CatJobHistoryItem[] | null }) =>
          (hist.jobs ?? [])
            .filter(
              (job) =>
                job.status === WorkStatus.PENDING ||
                job.status === WorkStatus.IN_PROGRESS
            )
            .map((job) => job.batch_id);

        const activeRmsEltIds = getActiveIds(rmsEltHist);
        const activeRms22Ids = getActiveIds(rms22Hist);
        const activeIcmYltIds = getActiveIds(icmYltHist);
        const activeSkIds = getActiveIds(skHist);

        if (activeRmsEltIds.length > 0) {
          rmsEltPolling.setPendingRequestIds(activeRmsEltIds);
        }
        if (activeRms22Ids.length > 0) {
          rms22Polling.setPendingRequestIds(activeRms22Ids);
        }
        if (activeIcmYltIds.length > 0) {
          icmYltPolling.setPendingRequestIds(activeIcmYltIds);
        }
        if (activeSkIds.length > 0) {
          spatialKatPolling.setPendingRequestIds(activeSkIds);
        }
      } catch (error) {
        console.log("No existing CAT results found");
      } finally {
        setIsInitialLoading(false);
      }
    };
    loadExistingResults();
  }, [specialtyPropertyInfo.report_id]);

  const rmsNeedsConfirmation =
    (precheck?.rms.warnings.length ?? 0) > 0 && !precheck?.rms.blocked;

  // Which model's runs can be promoted to the report's live results. SpatialKat
  // is absent from the endpoint's type union, so the button doesn't appear on
  // its history — see ActivatableCatTypeParam server-side for why.
  const activationModel: CatVendorType | null =
    selectedModel === "rms_elt" ||
    selectedModel === "rms_22" ||
    selectedModel === "icm_ylt"
      ? selectedModel
      : null;

  // Per-row recovery for the pipeline tabs (RMS / RMS 22 / ICM YLT), shown in
  // the Run History tab: each row resumes (failed) or re-runs financials (done)
  // against its own batch. All three ride the generic
  // /cat/{report_id}/{resume,rerun-financial} endpoints (the backend branches
  // on type), so EltPipelineType already covers icm_ylt. Other models don't.
  const eltRecoveryModel: EltPipelineType | null =
    rmsRerunFinancialsEnabled &&
    (selectedModel === "rms_elt" ||
      selectedModel === "rms_22" ||
      selectedModel === "icm_ylt")
      ? selectedModel
      : null;
  const eltPolling =
    eltRecoveryModel === "icm_ylt"
      ? icmYltPolling
      : eltRecoveryModel === "rms_22"
        ? rms22Polling
        : rmsEltPolling;
  const eltJobHistory =
    eltRecoveryModel === "icm_ylt"
      ? icmYltJobHistory
      : eltRecoveryModel === "rms_22"
        ? rms22JobHistory
        : rmsEltJobHistory;

  // The status poll refreshes job history every couple of seconds; this
  // flips the row over without waiting for the next tick. Never throws:
  // a failure here must not read as the action itself having failed.
  const refreshEltJobHistory = async () => {
    if (!eltRecoveryModel) return;
    try {
      const history = await eltJobHistory(reportId);
      if (history.jobs) {
        handleJobHistoryUpdate(eltRecoveryModel, history.jobs);
      }
    } catch {
      // the poll picks it up
    }
  };

  const handleRerunCatFinancials = async (
    sourceBatchId: string,
    activate: boolean
  ) => {
    if (!eltRecoveryModel) return;
    setRecoveryInFlightBatchId(sourceBatchId);
    try {
      const result = await rerunCatFinancials(
        reportId,
        eltRecoveryModel,
        sourceBatchId,
        activate
      );
      eltPolling.setPendingRequestIds((prev) => [...prev, result.batch_id]);
      toast.show({
        severity: "info",
        summary: activate
          ? "Financials Re-run Started"
          : "Shadow Financials Re-run Started",
        detail: activate
          ? "Re-running the financial model from the selected batch."
          : "Re-running financials without replacing active results.",
      });
      refreshEltJobHistory();
    } catch {
      toast.show({
        severity: "error",
        summary: "Re-run Failed",
        detail: "Could not start the financials re-run.",
      });
    } finally {
      setRecoveryInFlightBatchId(null);
    }
  };

  const handleResumeCatPipeline = async (
    batchId: string,
    mode: "resume" | "restart"
  ) => {
    if (!eltRecoveryModel) return;
    setRecoveryInFlightBatchId(batchId);
    try {
      const result = await resumeCatPipeline(
        reportId,
        eltRecoveryModel,
        batchId,
        mode
      );
      eltPolling.setPendingRequestIds((prev) =>
        prev.includes(result.batch_id) ? prev : [...prev, result.batch_id]
      );
      toast.show({
        severity: "info",
        summary: mode === "restart" ? "Run Restarted" : "Run Resumed",
        detail:
          mode === "restart"
            ? "Restarting modeling with fresh submissions."
            : "Resuming the selected batch from where it stalled.",
      });
      refreshEltJobHistory();
    } catch {
      toast.show({
        severity: "error",
        summary: mode === "restart" ? "Restart Failed" : "Resume Failed",
        detail:
          mode === "restart"
            ? "Could not restart the run."
            : "Could not resume the run.",
      });
    } finally {
      setRecoveryInFlightBatchId(null);
    }
  };

  const eltRecovery = eltRecoveryModel
    ? {
        onResume: (batchId: string) =>
          handleResumeCatPipeline(batchId, "resume"),
        // Restart (drop the stuck ICM request, submit fresh) only makes
        // sense for ICM YLT — the RMS pipelines have no ICM status URL to
        // shed, and the backend rejects restart for them.
        onRestart:
          eltRecoveryModel === "icm_ylt"
            ? (batchId: string) => handleResumeCatPipeline(batchId, "restart")
            : undefined,
        onRerunFinancials: handleRerunCatFinancials,
        shadowRerunAllowed: catShadowRunAllowed,
        actionInFlightBatchId: recoveryInFlightBatchId,
        disabled: eltPolling.isPending,
      }
    : null;

  const rmsBlocked = !precheck || precheck.rms.blocked;
  const spatialKatBlocked = !precheck || precheck.spatialkat.blocked;
  const icmYltBlocked = !precheck || precheck.icm.blocked;
  const rmsNotRunnable =
    rmsBlocked || (!catModelOverrideAllowed && rmsNeedsConfirmation);
  const openRunModelingDialog = () => {
    setRunModelingRms(!rmsNotRunnable);
    setRunModelingKatRisk(!spatialKatBlocked);
    setRunModelingIcm(!icmYltBlocked);
    setRunModelingRater(!raterBlocked);
    setRunModelingEqbd(!raterBlocked);
    setShowRunModelingDialog(true);
  };
  // ``mode`` overrides the ICM/RMS baseline run behavior (full remodel /
  // finance-only). It only reaches the server for ENG (the override menu is
  // ENG-gated); the other vendors ignore it.
  const handleRunModeling = async (mode?: CatRunMode) => {
    if (runModelingRms && isLargeJob && !largeCatJobsAllowed) {
      setShowRunModelingDialog(false);
      setShowLargeJobBlockedDialog(true);
      return;
    }
    setShowRunModelingDialog(false);
    const runs: Promise<unknown>[] = [];
    if (runModelingRms && !rmsNotRunnable) {
      runs.push(rmsEltPolling.handleInitiate(true, mode));
    }
    if (runModelingKatRisk && !spatialKatBlocked) {
      runs.push(spatialKatPolling.handleInitiate(true));
    }
    if (runModelingIcm && !icmYltBlocked) {
      runs.push(icmYltPolling.handleInitiate(true, mode));
    }
    if (runModelingRater && !raterBlocked) {
      runs.push(handleRunRater());
    }
    if (runModelingEqbd && !raterBlocked) {
      runs.push(handleRunEqbdRater());
    }
    await Promise.all(runs);
  };
  const runModelingLoading =
    rmsEltPolling.isInitiateLoading ||
    spatialKatPolling.isInitiateLoading ||
    icmYltPolling.isInitiateLoading ||
    runRater.isPending ||
    runEqbdRater.isPending;
  const runModelingDisabled =
    !runModelingRms &&
    !runModelingKatRisk &&
    !runModelingIcm &&
    !runModelingRater &&
    !runModelingEqbd;
  // Shared by the ENG SplitButton and the plain Button run controls.
  const runButtonProps = {
    label: "Run",
    icon: "pi pi-play",
    onClick: () => handleRunModeling(),
    loading: runModelingLoading,
    disabled: runModelingDisabled,
  };

  const layerInfoById = useMemo(
    () =>
      new Map(
        specialtyPropertyInfo.report_json.layer_info.map((layer) => [
          layer.layer_id!,
          layer,
        ])
      ),
    [specialtyPropertyInfo]
  );

  // The audit source for the current model, or null for models without a
  // financial-model audit (the rater / EB rater tabs). When null, run
  // comparison is unavailable.
  const compareAuditSource =
    selectedModel === "rater" || selectedModel === "eqbd"
      ? null
      : selectedModelToAuditSource(selectedModel);

  // The pool a comparison draws from.
  const compareCandidates = useMemo(
    () =>
      jobHistory.filter(
        (job) => job.type === selectedModel && job.status === WorkStatus.DONE
      ),
    [jobHistory, selectedModel]
  );

  const hasRmsEltResults =
    (rmsEltResultsData?.rms_elt?.aggregate?.layers?.length ?? 0) > 0;
  const hasRms22Results =
    (rms22ResultsData?.rms_22?.aggregate?.layers?.length ?? 0) > 0;
  const hasIcmYltResults =
    (icmYltResultsData?.icm_ylt?.aggregate?.layers?.length ?? 0) > 0;
  const hasSpatialKatResults =
    (spatialKatResultsData?.spatialkat?.aggregate?.layers?.length ?? 0) > 0;
  const hasResults =
    hasRmsEltResults ||
    hasRms22Results ||
    hasIcmYltResults ||
    hasSpatialKatResults;

  const handleViewJobResults = async (job: CatJobHistoryItem) => {
    try {
      const results = await catResults(
        specialtyPropertyInfo.report_id,
        job.batch_id
      );
      if (job.type === "rms_elt") {
        setRmsEltResultsData(results);
      } else if (job.type === "rms_22") {
        setRms22ResultsData(results);
      } else if (job.type === "icm_ylt") {
        setIcmYltResultsData(results);
      } else if (job.type === "spatialkat") {
        setSpatialKatResultsData(results);
      }
      setViewingEnhancedSource(job.enrichment_source ?? null);
      setViewingBatchId(job.batch_id);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load results for this job.",
      });
    }
  };

  const handleResetToDefaultResults = async () => {
    try {
      const results = await catResults(specialtyPropertyInfo.report_id);
      setRmsEltResultsData(results);
      setRms22ResultsData(results);
      setIcmYltResultsData(results);
      setSpatialKatResultsData(results);
      setViewingEnhancedSource(null);
      setViewingBatchId(null);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Load Failed",
        detail: "Could not load default results.",
      });
    }
  };

  // Adopt an already-completed run as this model's live results. The activated
  // batch is what the default view resolves to afterward, so the results reset
  // to default instead of staying pinned to whichever row was being inspected.
  const handleActivateRun = async (batchId: string, model: CatVendorType) => {
    setActivatingBatchId(batchId);
    try {
      await activateCatBatch(reportId, model, batchId);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Activation Failed",
        // The server's reason separates "a run is in flight" (retry once it
        // finishes) from "this run has no stored results" (retrying won't help).
        detail: apiErrorDetail(error) ?? "Could not make this run active.",
      });
      setActivatingBatchId(null);
      return;
    }
    // The flip has committed. Everything below only refreshes the view, so a
    // failure there must not report the activation itself as failed.
    toast.show({
      severity: "success",
      summary: "Run Activated",
      detail:
        "This run now backs the report's results; staleness re-checks against it.",
    });
    try {
      const history = await JOB_HISTORY_FETCHER[model](reportId);
      handleJobHistoryUpdate(model, history.jobs ?? []);
      await handleResetToDefaultResults();
      refetchPrecheck();
    } catch {
      toast.show({
        severity: "warn",
        summary: "View May Be Stale",
        detail: "The run was activated. Reload to see the updated results.",
      });
    } finally {
      setActivatingBatchId(null);
    }
  };

  const handleDownloadAll = async (job: CatJobHistoryItem) => {
    try {
      // Each cat source has its own GCS prefix, so the artifact download is
      // dispatched per type — a mismatched type would look up the wrong
      // prefix (silent empty zip) or name the file misleadingly.
      const downloadFnByType: Partial<
        Record<
          CatJobHistoryItem["type"],
          (
            reportId: string,
            batchId: string
          ) => Promise<{ data: Blob; filename?: string }>
        >
      > = {
        rms_elt: downloadAllRmsEltArtifacts,
        rms_22: downloadAllRms22Artifacts,
        icm_ylt: downloadAllIcmYltArtifacts,
        spatialkat: downloadAllSpatialKatArtifacts,
      };
      const downloadFn = downloadFnByType[job.type];
      if (!downloadFn) {
        return;
      }
      const { data, filename } = await downloadFn(
        specialtyPropertyInfo.report_id,
        job.batch_id
      );
      downloadBlob(filename ?? `${job.type}_all_${job.batch_id}.zip`, data);
    } catch (error) {
      toast.show({
        severity: "error",
        summary: "Download Failed",
        detail: "Could not download the artifacts zip.",
      });
    }
  };

  const raterPending = raterJobs.some((j) => !j.outcome);
  const isPending =
    rmsEltPolling.isPending ||
    rms22Polling.isPending ||
    icmYltPolling.isPending ||
    spatialKatPolling.isPending ||
    raterPending ||
    eqbdRaterPending;

  const pendingMessage = (() => {
    const active: string[] = [];
    if (rmsEltPolling.isPending) active.push("RMS");
    if (rms22Polling.isPending) active.push("RMS 22");
    if (spatialKatPolling.isPending) active.push("KatRisk");
    if (icmYltPolling.isPending) active.push("ICM");
    if (raterPending) active.push("Rater");
    if (eqbdRaterPending) active.push("EB Rater");
    if (active.length === 0) return "";
    const list =
      active.length === 1
        ? active[0]
        : active.length === 2
          ? `${active[0]} and ${active[1]}`
          : `${active.slice(0, -1).join(", ")}, and ${active[active.length - 1]}`;
    return `${list} in progress...`;
  })();

  const topLocationsByPeril =
    selectedModel === "rms_elt"
      ? rmsEltResultsData?.rms_elt?.top_locations_by_peril
      : selectedModel === "rms_22"
        ? rms22ResultsData?.rms_22?.top_locations_by_peril
        : selectedModel === "icm_ylt"
          ? icmYltResultsData?.icm_ylt?.top_locations_by_peril
          : undefined;

  return (
    <Card
      title={
        <div className={styles.titleRow}>
          <span className={styles.cardSectionTitle}>Modeling</span>
          <Button
            label={
              hasRmsEltResults ||
              hasSpatialKatResults ||
              hasIcmYltResults ||
              hasRaterResults ||
              hasEqbdResults
                ? "Re-run modeling"
                : "Run modeling"
            }
            icon="pi pi-play"
            raised
            text
            onClick={openRunModelingDialog}
            loading={isPending}
            disabled={
              !precheck ||
              (rmsNotRunnable &&
                spatialKatBlocked &&
                icmYltBlocked &&
                raterBlocked)
            }
          />
          {precheck &&
            selectedModel !== "rater" &&
            selectedModel !== "eqbd" && (
              <CatPrecheckBanner
                precheck={precheck}
                selectedModel={selectedModel}
              />
            )}
          {isMobile && (hasResults || jobHistory.length > 0) && (
            <div className={styles.headerLayerToggle}>
              <Checkbox
                inputId="show-all-layers-mobile"
                checked={showAllLayers}
                onChange={(e) => setShowAllLayers(!!e.checked)}
              />
              <label htmlFor="show-all-layers-mobile">Show all layers</label>
            </div>
          )}
        </div>
      }
      className={styles.catCard}
    >
      <div className={styles.content}>
        {isPending && (
          <Message
            severity="info"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <ProgressSpinner
                  className={styles.spinnerSmall}
                  strokeWidth="4"
                />
                <span>{pendingMessage}</span>
              </div>
            }
          />
        )}

        {viewingBatchId && (
          <Message
            severity="warn"
            className={styles.banner}
            content={
              <div className={styles.bannerContent}>
                <span>
                  Viewing results from a historical run — these are not the
                  active results.
                </span>
                <Button
                  label="Reset to default"
                  text
                  size="small"
                  onClick={handleResetToDefaultResults}
                />
              </div>
            }
          />
        )}

        {precheck &&
          catStalenessUiEnabled &&
          selectedModel !== "rater" &&
          selectedModel !== "eqbd" &&
          // RMS 22 isn't part of the normal flow, so don't surface staleness for
          // it on the modeling tab.
          // There could be some nuance here if we are pricing, but leave that
          // to the Layers tab to decide.
          selectedModel !== "rms_22" && (
            <CatRunStatusIndicator
              result={
                precheck[VENDOR_PRECHECK_KEY[selectedModel]].results_change
              }
            />
          )}

        <div className={styles.modelSelectionRow}>
          <CatModelSwitcher
            selectedModel={selectedModel}
            onChange={setSelectedModel}
            isMobile={isMobile}
            statusByModel={runStatusByModel}
          />
          {selectedModel === "rater" ? (
            <>
              <Button
                label={
                  raterTemplateOverride
                    ? `Generate rater (${raterTemplateOverride.template_name})`
                    : hasRaterResults
                      ? "Re-run rater"
                      : "Generate rater"
                }
                icon="pi pi-play"
                raised
                text
                severity="secondary"
                onClick={handleRunRater}
                loading={runRater.isPending}
                disabled={runRater.isPending || raterPending || raterBlocked}
              />
              <TemplateButton
                useCase="rater"
                suffix="rater"
                onOverrideTemplateVersion={setRaterTemplateOverride}
              />
            </>
          ) : selectedModel === "eqbd" ? (
            <Button
              label={hasEqbdResults ? "Re-run EB rater" : "Generate EB rater"}
              icon="pi pi-play"
              raised
              text
              severity="secondary"
              onClick={handleRunEqbdRater}
              loading={runEqbdRater.isPending}
              disabled={
                runEqbdRater.isPending || eqbdRaterPending || raterBlocked
              }
            />
          ) : selectedModel === "icm_ylt" ? (
            <>
              <Button
                label={hasIcmYltResults ? "Re-run ICM" : "Run ICM"}
                icon="pi pi-play"
                raised
                text
                severity="secondary"
                onClick={() => icmYltPolling.handleInitiate(true)}
                loading={icmYltPolling.isInitiateLoading}
                disabled={
                  icmYltPolling.isPending || !precheck || precheck.icm.blocked
                }
              />
              {catShadowRunAllowed && (
                <SplitButton
                  label="Shadow run"
                  icon="pi pi-clone"
                  outlined
                  severity="secondary"
                  onClick={() => icmYltPolling.handleInitiate(false)}
                  model={[
                    {
                      label: "Enhanced (Nearmap/HazardHub)",
                      icon: "pi pi-sparkles",
                      command: () => handleEnhancedIcmShadowRun("nearmap"),
                    },
                    {
                      label: "Enhanced (Cape/HazardHub)",
                      icon: "pi pi-sparkles",
                      command: () => handleEnhancedIcmShadowRun("cape"),
                    },
                  ]}
                  loading={icmYltPolling.isInitiateLoading || enrichLoading}
                  disabled={
                    icmYltPolling.isPending ||
                    enrichLoading ||
                    !precheck ||
                    precheck.icm.blocked
                  }
                  tooltip="Shadow run without replacing active results. Enhanced runs the Nearmap/HazardHub-enriched variant — inspect it in Run History."
                  tooltipOptions={{ position: "bottom" }}
                />
              )}
            </>
          ) : selectedModel === "rms_elt" ? (
            <>
              <Button
                label={hasRmsEltResults ? "Re-run RMS" : "Run RMS"}
                icon="pi pi-play"
                onClick={() =>
                  guardLargeJob(() => rmsEltPolling.handleInitiate(true))
                }
                raised
                text
                severity="secondary"
                loading={rmsEltPolling.isInitiateLoading}
                disabled={
                  rmsEltPolling.isPending ||
                  !precheck ||
                  precheck.rms.blocked ||
                  (!catModelOverrideAllowed && rmsNeedsConfirmation)
                }
              />
              {catShadowRunAllowed && (
                <SplitButton
                  label="Shadow run"
                  icon="pi pi-clone"
                  outlined
                  severity="secondary"
                  onClick={() =>
                    guardLargeJob(() => rmsEltPolling.handleInitiate(false))
                  }
                  model={[
                    {
                      label: "Enhanced (Nearmap/HazardHub)",
                      icon: "pi pi-sparkles",
                      command: () => handleEnhancedShadowRun("nearmap"),
                    },
                    {
                      label: "Enhanced (Cape/HazardHub)",
                      icon: "pi pi-sparkles",
                      command: () => handleEnhancedShadowRun("cape"),
                    },
                  ]}
                  loading={rmsEltPolling.isInitiateLoading || enrichLoading}
                  disabled={
                    rmsEltPolling.isPending ||
                    enrichLoading ||
                    !precheck ||
                    precheck.rms.blocked ||
                    (!catModelOverrideAllowed && rmsNeedsConfirmation)
                  }
                  tooltip="Shadow run without replacing active results. Enhanced runs the Nearmap/HazardHub-enriched variant — inspect it in Run History."
                  tooltipOptions={{ position: "bottom" }}
                />
              )}
            </>
          ) : selectedModel === "spatialkat" ? (
            <Button
              label={
                hasSpatialKatResults
                  ? "Re-run KatRisk Flood"
                  : "Run KatRisk Flood"
              }
              icon="pi pi-play"
              raised
              text
              severity="secondary"
              onClick={() => spatialKatPolling.handleInitiate(true)}
              loading={spatialKatPolling.isInitiateLoading}
              disabled={
                spatialKatPolling.isPending ||
                !precheck ||
                precheck.spatialkat.blocked
              }
            />
          ) : (
            <>
              <Button
                label={hasRms22Results ? "Re-run RMS 22" : "Run RMS 22"}
                icon="pi pi-play"
                onClick={() =>
                  guardLargeJob(() => rms22Polling.handleInitiate(true))
                }
                raised
                text
                severity="secondary"
                loading={rms22Polling.isInitiateLoading}
                disabled={
                  rms22Polling.isPending ||
                  !precheck ||
                  precheck.rms.blocked ||
                  (!catModelOverrideAllowed && rmsNeedsConfirmation)
                }
              />
              {catShadowRunAllowed && (
                <ShadowRunButton
                  onClick={() =>
                    guardLargeJob(() => rms22Polling.handleInitiate(false))
                  }
                  loading={rms22Polling.isInitiateLoading}
                  disabled={
                    rms22Polling.isPending ||
                    !precheck ||
                    precheck.rms_22.blocked ||
                    (!catModelOverrideAllowed && rmsNeedsConfirmation)
                  }
                />
              )}
            </>
          )}
        </div>
        {selectedModel === "rater" ? (
          <RaterTab reportId={reportId} />
        ) : selectedModel === "eqbd" ? (
          <EqbdRaterTab reportId={reportId} />
        ) : hasResults || jobHistory.length > 0 ? (
          <CatResultsView
            isMobile={isMobile}
            selectedModel={selectedModel}
            rmsEltResultsData={rmsEltResultsData}
            rms22ResultsData={rms22ResultsData}
            icmYltResultsData={icmYltResultsData}
            spatialKatResultsData={spatialKatResultsData}
            layerInfoById={layerInfoById}
            showAllLayers={showAllLayers}
            onShowAllLayersChange={setShowAllLayers}
            topLocationsByPeril={topLocationsByPeril}
            reportId={reportId}
            jobHistory={jobHistory}
            viewingBatchId={viewingBatchId}
            viewingEnhancedSource={viewingEnhancedSource}
            onViewJobResults={handleViewJobResults}
            onDownloadAll={handleDownloadAll}
            onCompareJob={compareAuditSource ? setCompareBaseJob : undefined}
            eltRecovery={eltRecovery}
            activation={
              catActivateRunAllowed && activationModel
                ? {
                    onActivate: (batchId: string) =>
                      handleActivateRun(batchId, activationModel),
                    inFlightBatchId: activatingBatchId,
                  }
                : null
            }
          />
        ) : isInitialLoading ? (
          <div className={styles.loadingContainer}>
            <ProgressSpinner />
          </div>
        ) : !isPending ? (
          <Message
            severity="info"
            text="No CAT results available yet. Run an analysis to generate results."
            className={styles.messageMargin}
          />
        ) : null}
      </div>

      {compareAuditSource && (
        <ModelRunComparisonDialog
          reportId={reportId}
          source={compareAuditSource}
          baseJob={compareBaseJob}
          candidates={compareCandidates}
          layerInfoById={layerInfoById}
          onHide={() => setCompareBaseJob(null)}
        />
      )}

      <Dialog
        header="Run Modeling"
        visible={showRunModelingDialog}
        onHide={() => setShowRunModelingDialog(false)}
        draggable={false}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              severity="secondary"
              text
              onClick={() => setShowRunModelingDialog(false)}
            />
            {catRunModeOverrideAllowed ? (
              // ENG escape hatch: primary Run uses smart staleness; the menu
              // forces a full remodel or a finance-only rerun on the ICM/RMS
              // baseline runs.
              <SplitButton
                {...runButtonProps}
                outlined
                severity="secondary"
                className={styles.runSplitButton}
                model={[
                  {
                    label: "Run — full remodel",
                    icon: "pi pi-refresh",
                    command: () => handleRunModeling("full"),
                  },
                  {
                    label: "Run — finance-only",
                    icon: "pi pi-dollar",
                    command: () => handleRunModeling("finance"),
                  },
                ]}
              />
            ) : (
              <Button {...runButtonProps} />
            )}
          </div>
        }
      >
        <div className={styles.runModelingOptions}>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-rms"
                checked={runModelingRms}
                onChange={(e) => setRunModelingRms(!!e.checked)}
                disabled={rmsNotRunnable}
              />
              <label htmlFor="run-modeling-rms">RMS</label>
            </div>
            {precheck && <PerilList vendor={precheck.rms} />}
          </div>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-katrisk"
                checked={runModelingKatRisk}
                onChange={(e) => setRunModelingKatRisk(!!e.checked)}
                disabled={spatialKatBlocked}
              />
              <label htmlFor="run-modeling-katrisk">KatRisk Flood</label>
            </div>
            {precheck && <PerilList vendor={precheck.spatialkat} />}
          </div>
          <div>
            <div className={styles.runModelingOption}>
              <Checkbox
                inputId="run-modeling-icm"
                checked={runModelingIcm}
                onChange={(e) => setRunModelingIcm(!!e.checked)}
                disabled={icmYltBlocked}
              />
              <label htmlFor="run-modeling-icm">ICM</label>
            </div>
            {precheck && <PerilList vendor={precheck.icm} />}
          </div>
          <div className={styles.runModelingOption}>
            <Checkbox
              inputId="run-modeling-rater"
              checked={runModelingRater}
              onChange={(e) => setRunModelingRater(!!e.checked)}
              disabled={raterBlocked}
            />
            <label htmlFor="run-modeling-rater">
              Fire Rater
              {raterTemplateOverride
                ? ` (${raterTemplateOverride.template_name})`
                : ""}
            </label>
          </div>
          <div className={styles.runModelingOption}>
            <Checkbox
              inputId="run-modeling-eqbd"
              checked={runModelingEqbd}
              onChange={(e) => setRunModelingEqbd(!!e.checked)}
              disabled={raterBlocked}
            />
            <label htmlFor="run-modeling-eqbd">EB Rater</label>
          </div>
        </div>
      </Dialog>

      <Dialog
        header="Large Submission"
        visible={showLargeJobBlockedDialog}
        onHide={() => setShowLargeJobBlockedDialog(false)}
        draggable={false}
        footer={
          <Button
            label="OK"
            onClick={() => setShowLargeJobBlockedDialog(false)}
          />
        }
      >
        <p>
          This submission has <b>{numLocations.toLocaleString()}</b> locations,
          which exceeds the {LARGE_CAT_JOB_THRESHOLD.toLocaleString()} location
          limit for running CAT jobs. Please ping John, Sean, or Andrew to run
          this for you.
        </p>
      </Dialog>
    </Card>
  );
};

const VENDOR_PRECHECK_KEY: Record<
  SelectedModel,
  "icm" | "rms" | "rms_22" | "spatialkat"
> = {
  icm_ylt: "icm",
  rms_elt: "rms",
  rms_22: "rms_22",
  spatialkat: "spatialkat",
};

const ShadowRunButton: React.FC<{
  onClick: () => void;
  loading: boolean;
  disabled: boolean;
}> = ({ onClick, loading, disabled }) => (
  <Button
    label="Shadow run"
    icon="pi pi-clone"
    tooltip="Runs the model without replacing the active results — inspect the batch in Run History."
    tooltipOptions={{ position: "bottom" }}
    outlined
    severity="secondary"
    onClick={onClick}
    loading={loading}
    disabled={disabled}
  />
);

const PerilList: React.FC<{ vendor: VendorPrecheck }> = ({ vendor }) => {
  const items = [
    ...vendor.run_perils.map((p) => ({ peril: p, run: true })),
    ...vendor.skipped_perils.map((p) => ({ peril: p, run: false })),
  ].sort((a, b) =>
    getPerilDisplayName(a.peril).localeCompare(getPerilDisplayName(b.peril))
  );
  if (items.length === 0) return null;
  return (
    <div className={styles.perilList}>
      {items.map(({ peril, run }) => (
        <div
          key={peril}
          className={`${styles.perilItem} ${
            run ? styles.perilItemRun : styles.perilItemSkip
          }`}
        >
          <i
            className={`pi ${run ? "pi-check" : "pi-times"} ${
              styles.perilItemIcon
            }`}
          />
          <span>{getPerilDisplayName(peril)}</span>
        </div>
      ))}
    </div>
  );
};

const CatPrecheckBanner: React.FC<{
  precheck: CatPrecheckResponse;
  selectedModel: SelectedModel;
}> = ({ precheck, selectedModel }) => {
  const vendor = precheck[VENDOR_PRECHECK_KEY[selectedModel]];
  const warnings = [...precheck.warnings, ...vendor.warnings];
  if (!warnings.length) return null;

  return (
    <Message
      severity={vendor.blocked ? "error" : "warn"}
      className={styles.precheckMessage}
      content={<span>{warnings.join(" ")}</span>}
    />
  );
};
